import { createSlice } from "@reduxjs/toolkit";
import { factory } from "../../api/apiFactory";
import { showNotification } from "./notificationSlice";
import dayjs from "dayjs";

const crmLeadApi = factory.get("crmLead");

const initialState = {
  leads: {
    loading: false,
    data: [],
    total: 0,
    filters: {
      page: 0,
      skip: 0,
      take: 15,
      search: "",
      status: [],
      source: "",
      governance_id: null,
      assigned_user_id: null,
      isp_id: null,
      start_date: null,
      end_date: null,
      reseller_name: "",
      name: "",
      phone: "",
    },
    dialog: false,
  },
  lead: {
    form: {
      id: "",
      ats_id: null,
      ats_name: "",
      full_name: "",
      phone: "",
      phone2: "",
      email: "",
      city: "",
      address: "",
      governance_id: null,
      governance_name: "",
      latitude: "",
      longitude: "",
      user_count: "",
      isp_id: null,
      isp_name: "",
      source: "",
      reseller_name: "",
      assigned_user_id: null,
      assigned_user_full_name: "",
      created_by: null,
      created_by_user_full_name: "",
      status: "new_lead",
      log_notes: "",
      notes: "",
      lost_reason: "",
      ticket_number: "",
    },
    dialog: false,
    loading: false,
    errors: {},
  },
  leadDetails: {
    loading: false,
    data: null,
  },
  enums: {
    loading: false,
    actions: [],
    sources: [],
    lost_reasons: [],
  },
  governances: {
    loading: false,
    data: [],
  },
  statusDialog: {
    open: false,
    id: null,
    status: "",
    lost_reason: "",
    loading: false,
  },
  commentDialog: {
    open: false,
    id: null,
    loading: false,
  },
  assignUserDialog: {
    open: false,
    id: null,
    loading: false,
  },
  exportLoading: {
    loading: false,
  },
};

export const crmLeadSlice = createSlice({
  name: "crmLead",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state[action.payload].loading = !state[action.payload].loading;
    },
    setLeads: (state, action) => {
      state.leads.data = action.payload.data.data;
      state.leads.total = action.payload.data.total;
    },
    setLead: (state, action) => {
      state.lead.form = { ...action.payload.data };
    },
    setLeadDetails: (state, action) => {
      state.leadDetails.data = action.payload.data.data[0] || action.payload.data.data;
    },
    resetForm: (state, action) => {
      state.lead.form = initialState.lead.form;
    },
    setFilters: (state, { payload }) => {
      state.leads.filters = {
        ...state.leads.filters,
        ...payload,
      };
    },
    setDialog: (state, { payload }) => {
      if (payload) {
        Object.keys(state.lead.form).forEach((key) => {
          state.lead.form[key] = payload[key] !== undefined ? payload[key] : state.lead.form[key];
        });
      } else {
        state.lead.form = initialState.lead.form;
      }
      state.lead.dialog = !state.lead.dialog;
    },
    resetFilters: (state) => {
      state.leads.filters = initialState.leads.filters;
    },
    clearLeads: (state) => {
      state.leads = initialState.leads;
    },
    setEnums: (state, action) => {
      state.enums.actions = action.payload.data.data.actions || [];
      state.enums.sources = action.payload.data.data.sources || [];
      state.enums.lost_reasons = action.payload.data.data.lost_reasons || [];
    },
    setGovernances: (state, action) => {
      state.governances.data = action.payload.data.data || [];
    },
    setStatusDialog: (state, { payload }) => {
      if (payload) {
        state.statusDialog.open = true;
        state.statusDialog.id = payload.id;
        state.statusDialog.status = payload.status || "";
        state.statusDialog.lost_reason = payload.lost_reason || "";
      } else {
        state.statusDialog.open = false;
        state.statusDialog.id = null;
        state.statusDialog.status = "";
        state.statusDialog.lost_reason = "";
      }
    },
    setCommentDialog: (state, { payload }) => {
      if (payload) {
        state.commentDialog.open = true;
        state.commentDialog.id = payload.id;
      } else {
        state.commentDialog.open = false;
        state.commentDialog.id = null;
      }
    },
    setAssignUserDialog: (state, { payload }) => {
      if (payload) {
        state.assignUserDialog.open = true;
        state.assignUserDialog.id = payload.id;
      } else {
        state.assignUserDialog.open = false;
        state.assignUserDialog.id = null;
      }
    },
    setCrmLeadFiltersDialog: (state) => {
      state.leads.dialog = !state.leads.dialog;
    },
  },
});

export const {
  setLoading,
  setLeads,
  setLead,
  setLeadDetails,
  setFilters,
  resetFilters,
  setDialog,
  resetForm,
  clearLeads,
  setEnums,
  setGovernances,
  setStatusDialog,
  setCommentDialog,
  setAssignUserDialog,
  setCrmLeadFiltersDialog,
} = crmLeadSlice.actions;

export default crmLeadSlice.reducer;

//axios thunks
const index = () => async (dispatch, getState) => {
  try {
    const filters = getState().crmLead.leads.filters;
    const filters_ = {
      ...filters,
      governance_id: filters.governance_id?.id || null,
      assigned_user_id: filters.assigned_user_id?.id || null,
      isp_id: filters.isp_id?.id || null,
      status: filters.status.length > 0 ? filters.status.join(",") : null,
    };
    dispatch(setLoading("leads"));
    const res = await crmLeadApi.index(filters_);
    dispatch(setLeads(res));
    dispatch(setLoading("leads"));
  } catch (err) {
    dispatch(setLoading("leads"));
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to fetch leads",
      })
    );
    throw new Error(err);
  }
};

const showById = (id) => async (dispatch, getState) => {
  try {
    dispatch(setLoading("leadDetails"));
    const res = await crmLeadApi.showById(id);
    dispatch(setLeadDetails(res));
    dispatch(setLoading("leadDetails"));
  } catch (err) {
    dispatch(setLoading("leadDetails"));
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to fetch lead details",
      })
    );
    throw new Error(err);
  }
};

const create = (data) => async (dispatch, getState) => {
  try {
    dispatch(setLoading("lead"));
    await crmLeadApi.create(data);
    dispatch(setLoading("lead"));
    dispatch(
      showNotification({
        type: "success",
        message: "CRM Lead Created Successfully",
      })
    );
    dispatch(setDialog());
    dispatch(resetForm());
    dispatch(index());
  } catch (err) {
    dispatch(setLoading("lead"));
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.message || err.response?.data?.errors?.[0] || "Failed to create lead",
      })
    );
    throw new Error(err);
  }
};

const update = (data) => async (dispatch, getState) => {
  try {
    const { id } = getState().crmLead.lead.form;
    dispatch(setLoading("lead"));
    await crmLeadApi.update(id, data);
    dispatch(setLoading("lead"));
    dispatch(
      showNotification({
        type: "success",
        message: "CRM Lead Updated Successfully",
      })
    );
    dispatch(setDialog());
    dispatch(resetForm());
    dispatch(index());
  } catch (err) {
    dispatch(setLoading("lead"));
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.message || err.response?.data?.errors?.[0] || "Failed to update lead",
      })
    );
    throw new Error(err);
  }
};

const Delete = (id) => async (dispatch, getState) => {
  try {
    dispatch(setLoading("lead"));
    await crmLeadApi.delete(id);
    dispatch(
      showNotification({
        type: "success",
        message: "CRM Lead Deleted Successfully",
      })
    );
    dispatch(index());
    dispatch(setLoading("lead"));
  } catch (err) {
    dispatch(setLoading("lead"));
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to delete lead",
      })
    );
    throw new Error(err);
  }
};

const fetchEnums = () => async (dispatch) => {
  try {
    dispatch(setLoading("enums"));
    const res = await crmLeadApi.getEnums();
    dispatch(setEnums(res));
    dispatch(setLoading("enums"));
  } catch (err) {
    dispatch(setLoading("enums"));
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to fetch enums",
      })
    );
    throw new Error(err);
  }
};

const fetchGovernances = () => async (dispatch) => {
  try {
    dispatch(setLoading("governances"));
    const res = await crmLeadApi.getGovernances();
    dispatch(setGovernances(res));
    dispatch(setLoading("governances"));
  } catch (err) {
    dispatch(setLoading("governances"));
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to fetch governances",
      })
    );
    throw new Error(err);
  }
};

const updateStatus = (data) => async (dispatch, getState) => {
  try {
    const { id } = getState().crmLead.statusDialog;
    dispatch(setLoading("statusDialog"));
    
    // Prepare status data with notes
    const statusData = {
      status: data.status,
    };
    
    // Add notes if provided
    if (data.notes) {
      statusData.notes = data.notes;
    }
    
    // Add reason only if status is closed_lost
    if (data.status === "closed_lost" && data.lost_reason) {
      statusData.reason = data.lost_reason;
    }
    
    await crmLeadApi.changeStatus(id, statusData);
    dispatch(setLoading("statusDialog"));
    dispatch(
      showNotification({
        type: "success",
        message: "Lead Status Updated Successfully",
      })
    );
    dispatch(setStatusDialog());
    // Refresh the leads table and lead details
    dispatch(index());
    dispatch(showById(id));
  } catch (err) {
    dispatch(setLoading("statusDialog"));
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to update status",
      })
    );
    throw new Error(err);
  }
};

const addComment = (data) => async (dispatch, getState) => {
  try {
    const { id } = getState().crmLead.commentDialog;
    dispatch(setLoading("commentDialog"));
    
    await crmLeadApi.addComment(id, { notes: data.notes });
    dispatch(setLoading("commentDialog"));
    dispatch(
      showNotification({
        type: "success",
        message: "Comment Added Successfully",
      })
    );
    dispatch(setCommentDialog());
    // Refresh the lead details
    dispatch(showById(id));
  } catch (err) {
    dispatch(setLoading("commentDialog"));
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to add comment",
      })
    );
    throw new Error(err);
  }
};

const exportExcel = () => async (dispatch, getState) => {
  try {
    const { page, skip, take, ...rest } = getState().crmLead.leads.filters;
    dispatch(setLoading("exportLoading"));
    const res = await crmLeadApi.exportToExcel({
      ...rest,
      governance_id: rest.governance_id?.id || null,
      assigned_user_id: rest.assigned_user_id?.id || null,
      isp_id: rest.isp_id?.id || null,
      status: rest.status?.length > 0 ? rest.status.join(",") : null,
      export: "excel",
    });
    let url = window.URL.createObjectURL(res.data);
    let a = document.createElement("a");
    a.href = url;
    a.download = `crm-leads_${dayjs().format("YYYY-MM-DD HH:mm")}.xlsx`;
    a.click();
    dispatch(setLoading("exportLoading"));
  } catch (err) {
    dispatch(setLoading("exportLoading"));
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to export leads",
      })
    );
  }
};

const assignUser = (data) => async (dispatch, getState) => {
  try {
    const { id } = getState().crmLead.assignUserDialog;
    dispatch(setLoading("assignUserDialog"));
    
    await crmLeadApi.assignUser(id, { assigned_user_id: data.assigned_user_id?.id });
    dispatch(setLoading("assignUserDialog"));
    dispatch(
      showNotification({
        type: "success",
        message: "User Assigned Successfully",
      })
    );
    dispatch(setAssignUserDialog());
    // Refresh the lead details
    dispatch(showById(id));
  } catch (err) {
    dispatch(setLoading("assignUserDialog"));
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to assign user",
      })
    );
    throw new Error(err);
  }
};

export const CrmLead = {
  index,
  showById,
  create,
  update,
  Delete,
  fetchEnums,
  fetchGovernances,
  updateStatus,
  addComment,
  assignUser,
  exportExcel,
};
